import { EnterpriseProfileType } from "@mg/schemas/src/commons";
import { Check } from "@phosphor-icons/react";
import {
  Dialog,
  Button,
  TextField,
  Flex,
  Text,
  Checkbox,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAnalytics } from "../utils/analytics";
import {
  enterpriseNameToSlug,
  enterpriseSlugProblem,
  findEnterpriseIdentityConflict,
} from "../utils/enterpriseDuplicate";
import { useAppSelector } from "../utils/hooks";
import { errorMessage } from "../utils/http";
import { stashPendingNotification } from "../utils/pendingNotifications";
import {
  useCreateEnterprise,
  useEnterprises,
  useSwitchEnterprise,
} from "../utils/queries/enterprises";
import { BaseDialogProps } from "../utils/types/dialog";

export function CreateEnterpriseDialog(props: BaseDialogProps) {
  const { t } = useTranslation("ui");
  const { open, onOpenChange } = props;
  const setOpen = onOpenChange;
  const [enterpriseName, setEnterpriseName] = useState("");
  const [enterpriseSlug, setEnterpriseSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const createEnterpriseMutation = useCreateEnterprise();
  const enterprisesQuery = useEnterprises();
  const posthog = useAnalytics("CreateEnterpriseDialog");
  const user = useAppSelector((state) => state.auth.value);
  const switchEnterpriseMutation = useSwitchEnterprise();

  useEffect(() => {
    if (slugTouched) return;
    setEnterpriseSlug(enterpriseNameToSlug(enterpriseName));
  }, [enterpriseName, slugTouched]);

  if (user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS) {
    return null;
  }

  const trimmedName = enterpriseName.trim();
  const trimmedSlug = enterpriseSlug.trim();
  const conflict = findEnterpriseIdentityConflict(enterprisesQuery.data ?? [], {
    name: trimmedName,
    slug: trimmedSlug,
  });
  const slugProblem = enterpriseSlugProblem(trimmedSlug);
  const nameError =
    conflict === "name"
      ? t("createEnterpriseDialog.nameTaken", { name: trimmedName })
      : null;
  const slugError =
    conflict === "slug"
      ? t("createEnterpriseDialog.slugTaken", { slug: trimmedSlug })
      : slugProblem === "length"
        ? t("settings.enterpriseSettingsView.slugMaxLength")
        : slugProblem === "charset"
          ? t(
              "settings.enterpriseSettingsView.useLowercaseLettersNumbersHyphens",
            )
          : null;
  const canSave =
    trimmedName.length > 0 &&
    trimmedSlug.length > 0 &&
    !nameError &&
    !slugError &&
    !createEnterpriseMutation.isPending;

  const handleSave = async () => {
    if (!canSave) return;
    setServerError(null);
    try {
      const enterprise = await createEnterpriseMutation.mutateAsync({
        name: trimmedName,
        slug: trimmedSlug,
        isDemo,
      });
      posthog.capture("created_new_enterprise", {
        name: trimmedName,
        isDemo,
      });
      // Stash the success toast in sessionStorage instead of calling
      // `notify()` directly. The auto-switch below triggers a hard
      // `window.location.reload()`, which tears down the React tree
      // and kills any queued snackbar with it. Users (and the
      // `enterprises.spec.ts` E2E test) previously saw the toast
      // flash for ~1 second before the reload wiped it — perceptibly
      // identical to "no toast at all". The stash survives the
      // reload; the remounted `UIProvider` drains the queue and fires
      // the toast on the new page. See
      // `frontends/carrie/src/utils/pendingNotifications.ts`.
      stashPendingNotification({
        variant: "success",
        title: "Enterprise Created",
        message: `Successfully created enterprise: ${trimmedName}`,
      });
      setOpen?.(false);
      setEnterpriseName("");
      setEnterpriseSlug("");
      setSlugTouched(false);
      setIsDemo(false);
      setServerError(null);
      switchEnterpriseMutation.mutate(enterprise._id as string, {
        onSuccess: () => {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
      });
    } catch (error) {
      console.error("Failed to create enterprise:", error);
      // A 409 is usually an enterprise this user is not a member of, so
      // the client-side list could not have warned them. Keep the dialog
      // open and show the server message instead of a toast the reload
      // path is not taking.
      const message = (await errorMessage(error)).trim();
      setServerError(message || t("failedCreateEnterprise"));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Content style={{ maxWidth: 450 }}>
        <Dialog.Title>{t("createNewEnterprise")}</Dialog.Title>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          data-testid="create-enterprise-form"
          aria-label={t("createNewEnterprise")}
        >
          <Flex direction="column" gap="3">
            <label htmlFor="enterprise-name">
              <Text as="div" size="2" mb="1" weight="bold">
                {t("enterpriseName")}
              </Text>
              <TextField.Root
                id="enterprise-name"
                name="enterprise-name"
                value={enterpriseName}
                onChange={(e) => setEnterpriseName(e.target.value)}
                data-testid="create-enterprise-name-input"
                aria-invalid={nameError ? true : undefined}
                aria-describedby={
                  nameError ? "create-enterprise-name-error" : undefined
                }
              />
              {nameError ? (
                <Text
                  id="create-enterprise-name-error"
                  data-testid="create-enterprise-name-error"
                  size="1"
                  color="red"
                  mt="1"
                >
                  {nameError}
                </Text>
              ) : null}
            </label>
            <label htmlFor="create-enterprise-slug">
              <Text as="div" size="2" mb="1" weight="bold">
                {t("createEnterpriseDialog.slug")}
              </Text>
              <TextField.Root
                id="create-enterprise-slug"
                name="create-enterprise-slug"
                value={enterpriseSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setEnterpriseSlug(e.target.value);
                }}
                data-testid="create-enterprise-slug-input"
                aria-label={t("createEnterpriseDialog.slug")}
                aria-invalid={slugError ? true : undefined}
                aria-describedby={
                  slugError ? "create-enterprise-slug-error" : undefined
                }
              />
              {slugError ? (
                <Text
                  id="create-enterprise-slug-error"
                  data-testid="create-enterprise-slug-error"
                  size="1"
                  color="red"
                  mt="1"
                >
                  {slugError}
                </Text>
              ) : null}
            </label>
            <label
              htmlFor="create-enterprise-demo-checkbox"
              className="flex cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="create-enterprise-demo-checkbox"
                checked={isDemo}
                onCheckedChange={(checked) => setIsDemo(checked === true)}
                data-testid="create-enterprise-demo-checkbox"
                aria-label={t("createEnterpriseDialog.demoEnterprise")}
              />
              <Text size="2">{t("createEnterpriseDialog.demoEnterprise")}</Text>
            </label>
            {serverError ? (
              <Text
                role="alert"
                size="2"
                color="red"
                data-testid="create-enterprise-error"
              >
                {serverError}
              </Text>
            ) : null}
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button type="button" variant="soft" color="gray">
                {t("cancel")}
              </Button>
            </Dialog.Close>
            <Button
              type="submit"
              disabled={!canSave}
              loading={createEnterpriseMutation.isPending}
            >
              <Check />
              {t("save")}
            </Button>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
